import React, { useContext, useEffect } from "react";
import { Global } from "@emotion/react";
import { styled } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import LaunchIcon from '@mui/icons-material/Launch';
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import Box from "@mui/material/Box";
import FormComponent from "./Form";
import { LogContext, initialPostData } from "../../../../context/LogContext";
import {isMobile} from "react-device-detect";
import {Container} from "@mui/material";


const drawerBleeding = 56;

interface Props {
  window?: () => Window;
  id?: string;
  option?: any; // Change 'any' to the specific type if possible
  event?: any; // Change 'any' to the specific type if possible
  setEvent?: React.Dispatch<React.SetStateAction<any>>; // Change 'any' to the specific type if possible
  isReadOnly: boolean,
  scrollToPost: (postId: string) => void | undefined
}

const StyledBox = styled("div")(({ theme }) => ({
  backgroundColor: "#222529",
}));

const Puller = styled("div")(({ theme }) => ({
  width: 30,
  height: 6,
  backgroundColor: "#6c7075",
  borderRadius: 3,
  position: "absolute",
  top: 8,
  left: "calc(50% - 15px)",
}));

const Drawer = (props: Props) => {
  const { editMode, postData } = useContext(LogContext);
  const { window } = props;
  const [open, setOpen] = React.useState(false);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  // For the puller/header area
  const handleToggle = () => {
    setOpen(!open);
  };

  useEffect(() => {
    if (editMode) {
      setOpen(true); // Open the drawer if editMode is true
    }
  }, [editMode]);

  // Get proper container reference
  const getContainer = React.useCallback(() => {
    return window !== undefined ? window().document.body : undefined;
  }, [window]);

  return (
    <div style={{zIndex: '10005'}}>
      <CssBaseline />
      <Global
        styles={{
          ".MuiDrawer-root > .MuiPaper-root": {
            height: `calc(80% - ${drawerBleeding}px)`,
            overflow: "visible",
            backgroundColor: "gray",
          },
        }}
      />
      
      {/* Fixed swipe area when drawer is closed */}
      {!open && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            height: drawerBleeding,
            backgroundColor: '#222529',
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            visibility: 'visible',
            zIndex: 1000,
            cursor: 'pointer',
          }}
          onClick={handleOpen}
        >
          <Puller />
          <Container sx={{ p: 2, color: "white", minHeight: drawerBleeding, display: 'flex', flexDirection: 'row', justifyContent: 'space-between', width: 1 }}>
            <Typography>Swipe up for log entry</Typography>
          </Container>
        </Box>
      )}

      <SwipeableDrawer
        container={getContainer}
        anchor="bottom"
        open={open}
        onClose={handleClose}
        onOpen={handleOpen}
        disableSwipeToOpen={false}
        swipeAreaWidth={drawerBleeding}
        disableDiscovery={false}
        keepMounted={true}
      >
        <StyledBox
          onClick={handleToggle}
          sx={{
            position: "absolute",
            top: -drawerBleeding,
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            visibility: "visible",
            right: 0,
            left: 0,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: !open ? 'flex-start' : 'flex-end',
          }}
        >
          <Puller />
          <Container sx={{ p: 2, color: "white", minHeight: drawerBleeding, display: 'flex', flexDirection: 'row', justifyContent: 'space-between', width: 1 }}>
              {!open && (<Typography>
                  Swipe up for a long entry
              </Typography>)}
            {props.isReadOnly && open && (
                <div 
                  onClick={(e) => {
                    e.stopPropagation();
                    if (props.scrollToPost) {
                      props.scrollToPost(postData.PostId);
                    }
                  }}
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    color: 'white',
                    marginRight: '15px',
                    alignItems: 'center',
                    cursor: 'pointer'
                  }}
                >
                  <Typography>Go to post</Typography>
                  <LaunchIcon sx={{ml: 1}}/>
                </div>
            )}
          </Container>
        </StyledBox>
        <StyledBox
            sx={{
                px: 2,
                pb: 2,
                height: "100%",
                overflow: "auto",
            }}
        >
          <FormComponent
            id={props.id}
            event={props.event}
            setEvent={props.setEvent}
            isReadOnly={props.isReadOnly}
          />
        </StyledBox>
      </SwipeableDrawer>
    </div>
  );
}

export default Drawer;
